import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

/**
 * PortfolioDataContext - Manages user's portfolio data (documents, profile, preferences)
 */
const PortfolioDataContext = createContext(null);

export const PortfolioDataProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // Portfolio Data State
  const [userProfile, setUserProfile] = useState(null);
  const [portfolioSections, setPortfolioSections] = useState({
    personalInfo: null,
    academicRecords: [],
    personalProjects: [],
    volunteeringExperience: [],
    skillsLanguages: [],
    extracurricularActivities: [],
  });
  const [preferences, setPreferences] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user profile
  const loadUserProfile = useCallback(async () => {
    // Skip remote fetch to avoid 400/406; rely on local defaults
    setUserProfile({});
  }, []);

  // Load portfolio sections
  const loadPortfolioSections = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('portfolio_sections')
        .select('*')
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      // Organize by section type
      const organized = {
        personalInfo: null,
        academicRecords: [],
        personalProjects: [],
        volunteeringExperience: [],
        skillsLanguages: [],
        extracurricularActivities: [],
      };

      data?.forEach((section) => {
        const key = getSectionKey(section.category);
        if (section.category === 'Personal Information') {
          organized.personalInfo = section;
        } else {
          organized[key].push(section);
        }
      });

      setPortfolioSections(organized);
    } catch (err) {
      // Silently handle missing table errors and RLS issues
      if (err.code !== 'PGRST205' && err.status !== 406) {
        console.error('Error loading portfolio sections:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load processed documents
  const loadProcessedDocuments = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('processed_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setDocuments(data || []);
    } catch (err) {
      // Silently handle missing table errors and RLS issues
      if (err.code !== 'PGRST205' && err.status !== 406) {
        console.error('Error loading documents:', err);
        setError(err.message);
      }
    }
  }, [user?.id]);

  // Load user preferences
  const loadPreferences = useCallback(async () => {
    // Skip querying user_preferences to avoid 406s; use safe defaults for now
    setPreferences({
      theme: 'light',
      notifications: true,
      language: 'en',
      auto_save: true,
      privacy_level: 'private',
    });
  }, []);

  // Load all data on authentication
  useEffect(() => {
    if (!isAuthenticated) {
      setUserProfile(null);
      setPortfolioSections({
        personalInfo: null,
        academicRecords: [],
        personalProjects: [],
        volunteeringExperience: [],
        skillsLanguages: [],
        extracurricularActivities: [],
      });
      setPreferences(null);
      setDocuments([]);
      return;
    }

    const loadAllData = async () => {
      await Promise.all([
        loadUserProfile(),
        loadPortfolioSections(),
        loadProcessedDocuments(),
        loadPreferences(),
      ]);
    };

    loadAllData();
  }, [isAuthenticated, user?.id]);

  // Update user profile
  const updateUserProfile = useCallback(
    async (updates) => {
      if (!user?.id) return;

      try {
        setError(null);
        const { data, error: updateError } = await supabase
          .from('users')
          .update(updates)
          .eq('id', user.id)
          .select()
          .single();

        if (updateError) throw updateError;

        setUserProfile(data);
        return data;
      } catch (err) {
        console.error('Error updating profile:', err);
        setError(err.message);
        throw err;
      }
    },
    [user?.id]
  );

  // Record marketing contact (name + phone) into a separate table for marketing uses
  const recordMarketingContact = useCallback(
    async ({ full_name, phone_prefix, phone_number }) => {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('marketing_contacts')
          .insert([
            {
              user_id: user.id,
              full_name: full_name || null,
              phone_prefix: phone_prefix || null,
              phone_number: phone_number || null,
              created_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (err) {
        console.error('Error recording marketing contact:', err);
        throw err;
      }
    },
    [user?.id]
  );

  // Update portfolio section
  const updatePortfolioSection = useCallback(
    async (sectionId, updates) => {
      if (!user?.id) return;

      try {
        setError(null);
        const { data, error: updateError } = await supabase
          .from('portfolio_sections')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', sectionId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Update local state
        updatePortfolioSectionsState(data);
        return data;
      } catch (err) {
        console.error('Error updating portfolio section:', err);
        setError(err.message);
        throw err;
      }
    },
    [user?.id]
  );

  // Add portfolio section
  const addPortfolioSection = useCallback(
    async (category, content) => {
      if (!user?.id) return;

      try {
        setError(null);
        const { data, error: insertError } = await supabase
          .from('portfolio_sections')
          .insert([
            {
              user_id: user.id,
              category,
              content,
              order: Date.now(),
            },
          ])
          .select()
          .single();

        if (insertError) throw insertError;

        // Update local state
        updatePortfolioSectionsState(data);
        return data;
      } catch (err) {
        console.error('Error adding portfolio section:', err);
        setError(err.message);
        throw err;
      }
    },
    [user?.id]
  );

  // Delete portfolio section
  const deletePortfolioSection = useCallback(
    async (sectionId, category) => {
      if (!user?.id) return;

      try {
        setError(null);
        const { error: deleteError } = await supabase
          .from('portfolio_sections')
          .delete()
          .eq('id', sectionId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        // Update local state
        const key = getSectionKey(category);
        if (category === 'Personal Information') {
          setPortfolioSections((prev) => ({
            ...prev,
            personalInfo: null,
          }));
        } else {
          setPortfolioSections((prev) => ({
            ...prev,
            [key]: prev[key].filter((s) => s.id !== sectionId),
          }));
        }
      } catch (err) {
        console.error('Error deleting portfolio section:', err);
        setError(err.message);
        throw err;
      }
    },
    [user?.id]
  );

  // Update preferences
  const updatePreferences = useCallback(
    async (newPreferences) => {
      if (!user?.id) return;

      try {
        setError(null);

        // Check if preferences exist
        const { data: existing } = await supabase
          .from('user_preferences')
          .select('id')
          .eq('user_id', user.id)
          .single();

        let result;
        if (existing) {
          const { data, error: updateError } = await supabase
            .from('user_preferences')
            .update(newPreferences)
            .eq('user_id', user.id)
            .select()
            .single();

          if (updateError) throw updateError;
          result = data;
        } else {
          const { data, error: insertError } = await supabase
            .from('user_preferences')
            .insert([{ user_id: user.id, ...newPreferences }])
            .select()
            .single();

          if (insertError) throw insertError;
          result = data;
        }

        setPreferences(result);
        return result;
      } catch (err) {
        console.error('Error updating preferences:', err);
        setError(err.message);
        throw err;
      }
    },
    [user?.id]
  );

  // Helper function to update portfolio sections state
  const updatePortfolioSectionsState = (updatedSection) => {
    const key = getSectionKey(updatedSection.category);

    if (updatedSection.category === 'Personal Information') {
      setPortfolioSections((prev) => ({
        ...prev,
        personalInfo: updatedSection,
      }));
    } else {
      setPortfolioSections((prev) => {
        const existing = prev[key].findIndex((s) => s.id === updatedSection.id);
        if (existing >= 0) {
          const updated = [...prev[key]];
          updated[existing] = updatedSection;
          return { ...prev, [key]: updated };
        } else {
          return { ...prev, [key]: [...prev[key], updatedSection] };
        }
      });
    }
  };

  // Subscribe to real-time changes
  useEffect(() => {
    if (!user?.id) return;

    const subscriptions = [];

    // Subscribe to user profile changes
    subscriptions.push(
      supabase
        .channel(`users:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'users',
            filter: `id=eq.${user.id}`,
          },
          (payload) => {
            if (payload.eventType === 'UPDATE') {
              setUserProfile(payload.new);
            }
          }
        )
        .subscribe()
    );

    // Subscribe to portfolio sections changes
    subscriptions.push(
      supabase
        .channel(`portfolio_sections:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'portfolio_sections',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              updatePortfolioSectionsState(payload.new);
            } else if (payload.eventType === 'UPDATE') {
              updatePortfolioSectionsState(payload.new);
            } else if (payload.eventType === 'DELETE') {
              const key = getSectionKey(payload.old.category);
              setPortfolioSections((prev) => ({
                ...prev,
                [key]: prev[key].filter((s) => s.id !== payload.old.id),
              }));
            }
          }
        )
        .subscribe()
    );

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [user?.id]);

  const value = {
    // State
    userProfile,
    portfolioSections,
    preferences,
    documents,
    loading,
    error,

    // Methods
    updateUserProfile,
    recordMarketingContact,
    updatePortfolioSection,
    addPortfolioSection,
    deletePortfolioSection,
    updatePreferences,
    loadUserProfile,
    loadPortfolioSections,
    loadProcessedDocuments,
    loadPreferences,
  };

  return (
    <PortfolioDataContext.Provider value={value}>
      {children}
    </PortfolioDataContext.Provider>
  );
};

export const usePortfolioData = () => {
  const context = useContext(PortfolioDataContext);
  if (!context) {
    throw new Error('usePortfolioData must be used within PortfolioDataProvider');
  }
  return context;
};

// Helper function to convert category to state key
function getSectionKey(category) {
  const mapping = {
    'Academic Records': 'academicRecords',
    'Personal Projects': 'personalProjects',
    'Volunteering Experience': 'volunteeringExperience',
    'Skills & Languages': 'skillsLanguages',
    'Extracurricular Activities': 'extracurricularActivities',
  };
  return mapping[category] || category;
}
